/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, CloseButton } from "react-bootstrap";
import "./ListUsersSession.css";
import { KTSVG, toAbsoluteUrl } from "../../../../_metronic/helpers";
import Print from "../../../components/Print";
import {
  OrderResponse,
  fetchUserById,
  findUserForOrder,
  formatUserFullName,
  formatUsuarioNomeSobrenome,
  getOrderClientUserId,
  useOrders,
} from "../../../modules/hooks/useOrders";
import "./ListUserPaymentHistory.css";
import { ClienteDadosModal } from "../../../modules/cardapio/components/ClienteDadosModal";
import { fetchClienteById } from "../../../modules/cardapio/utils/cardapio.api";

interface ListUserPaymentHistoryProps {
  inputVisible: boolean;
}

const PATH_URL = "https://companies-admin.aacceson.com/";

function normalizeStatus(status: string | undefined): "Pago" | "Em aberto" {
  const raw = String(status || "").toLowerCase();
  if (raw.includes("pago") || raw.includes("paid") || raw.includes("completed")) {
    return "Pago";
  }
  return "Em aberto";
}

function formatOrderDateTime(order: OrderResponse): string {
  let day = "";
  let month = "";
  let hhmm = "";

  const isoMatch = String(order.data || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    day = isoMatch[3];
    month = isoMatch[2];
  } else {
    const brMatch = String(order.data || "").match(/^(\d{2})\/(\d{2})/);
    if (brMatch) {
      day = brMatch[1];
      month = brMatch[2];
    }
  }

  const hora = String(order.hora || "").trim();
  const timeMatch = hora.match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    hhmm = `${timeMatch[1].padStart(2, "0")}${timeMatch[2]}`;
  } else if (/^\d{3,4}$/.test(hora)) {
    hhmm = hora.padStart(4, "0");
  } else if (order.data_hora_unix) {
    const d = new Date(order.data_hora_unix * 1000);
    day = String(d.getDate()).padStart(2, "0");
    month = String(d.getMonth() + 1).padStart(2, "0");
    hhmm = `${String(d.getHours()).padStart(2, "0")}${String(
      d.getMinutes(),
    ).padStart(2, "0")}`;
  }

  if (!day || !month) return [hora, hhmm].filter(Boolean).join(" ") || "—";
  return hhmm ? `${day}/${month} ${hhmm}` : `${day}/${month}`;
}

const ListUserPaymentHistory: React.FC<ListUserPaymentHistoryProps> = ({
  inputVisible,
}) => {
  const { isLoading, orders, userData } = useOrders();
  const [filterName, setFilterName] = useState("");
  const [filterOption, setFilterOption] = useState("all");
  const [modalPicture, setModalPicture] = useState(false);
  const [modalCadastro, setModalCadastro] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [extraUsersById, setExtraUsersById] = useState<Record<number, any>>(
    {},
  );
  /** Nome = u.nome + ' ' + u.sobrenome (companies-users.usuarios). */
  const [clienteNamesById, setClienteNamesById] = useState<
    Record<number, string>
  >({});

  const allUsers = useMemo(() => {
    const map = new Map<number, any>();
    for (const u of userData || []) {
      const id = Number(u?._id ?? u?.id ?? 0);
      if (id > 0) map.set(id, u);
    }
    for (const [id, u] of Object.entries(extraUsersById)) {
      map.set(Number(id), u);
    }
    return Array.from(map.values());
  }, [userData, extraUsersById]);

  useEffect(() => {
    if (!orders?.length) return;
    const ids = Array.from(
      new Set(
        orders
          .map((order) => getOrderClientUserId(order))
          .filter((id) => id > 0 && !clienteNamesById[id]),
      ),
    );
    if (!ids.length) return;

    let cancelled = false;
    (async () => {
      const entries: Record<number, string> = {};
      await Promise.all(
        ids.map(async (id) => {
          const cliente = await fetchClienteById(id).catch(() => null);
          if (cliente) {
            entries[id] = formatUsuarioNomeSobrenome(
              cliente.nome,
              cliente.sobrenome,
            );
          }
        }),
      );
      if (!cancelled && Object.keys(entries).length) {
        setClienteNamesById((prev) => ({ ...prev, ...entries }));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  useEffect(() => {
    if (!orders?.length) return;
    const missingIds = Array.from(
      new Set(
        orders
          .map((order) => {
            if (findUserForOrder(order, allUsers)) return 0;
            return getOrderClientUserId(order);
          })
          .filter((id) => id > 0 && !extraUsersById[id]),
      ),
    );
    if (!missingIds.length) return;

    let cancelled = false;
    (async () => {
      const entries: Record<number, any> = {};
      await Promise.all(
        missingIds.map(async (id) => {
          const user = await fetchUserById(id);
          if (user) entries[id] = user;
        }),
      );
      if (!cancelled && Object.keys(entries).length) {
        setExtraUsersById((prev) => ({ ...prev, ...entries }));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, userData]);

  useEffect(() => {
    if (!inputVisible) setFilterName("");
  }, [inputVisible]);

  const resolveUser = useCallback(
    (order: OrderResponse) => findUserForOrder(order, allUsers),
    [allUsers],
  );

  const resolveName = useCallback(
    (order: OrderResponse) => {
      const userId = getOrderClientUserId(order);
      if (userId && clienteNamesById[userId]) {
        return clienteNamesById[userId];
      }
      const user = findUserForOrder(order, allUsers);
      const fromUser = formatUserFullName(user);
      if (fromUser) return fromUser;
      return "Cliente";
    },
    [allUsers, clienteNamesById],
  );

  const getUserImageURL = (order: OrderResponse) => {
    const user = resolveUser(order);
    if (user?.profile_image) {
      const img = String(user.profile_image);
      if (img.startsWith("http") || img.startsWith("data:")) return img;
      return `${PATH_URL}${img}`;
    }
    return "/media/avatars/300-3.jpg";
  };

  const getUserBraceletColor = (order: OrderResponse) => {
    const user = resolveUser(order);
    return user?.bracelets?.[0]?.bracelet_color?.color || "#FFF";
  };

  const openPhoto = (order: OrderResponse) => {
    setSelectedUser(resolveUser(order));
    setModalPicture(true);
  };

  const openCadastro = (order: OrderResponse) => {
    const user = resolveUser(order);
    const userId = Number(user?._id ?? user?.id ?? getOrderClientUserId(order));
    if (!userId) return;
    setSelectedUser(user || { _id: userId });
    setSelectedUserId(userId);
    setModalCadastro(true);
  };

  const filteredOrders = (orders || []).filter((order) => {
    const fullName = resolveName(order).toLowerCase();
    if (!fullName.includes(filterName.toLowerCase())) return false;
    const status = normalizeStatus(order.status_pedido);
    if (filterOption === "pending") return status === "Em aberto";
    if (filterOption === "completed") return status === "Pago";
    return true;
  });

  if (isLoading) {
    return (
      <div className="card-body h-250px overflow-auto overflow-x-scroll">
        <div className="d-flex flex-column flex-center h-100">
          <div className="text-center">
            <span className="svg-icon svg-icon-2x svg-icon-primary">
              <KTSVG
                path="/media/icons/duotune/general/gen019.svg"
                className="svg-icon svg-icon-2x svg-icon-primary"
              />
            </span>
            <div className="text-gray-600 fw-bold fs-6 mt-5">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-body h-250px overflow-auto overflow-x-scroll">
      {inputVisible && (
        <>
          <div
            className={`fade-in ${
              inputVisible ? "active" : ""
            } w-100 d-flex flex-row justify-content-center align-items-center p-1`}
          >
            <span className="menu-icon me-3">
              <KTSVG
                path="/media/icons/duotune/general/gen004.svg"
                className="svg-icon-1 "
              />
            </span>
            <input
              type="text"
              className="form-control form-control-solid w-100 "
              placeholder="Filtrar por nome"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-sm me-1 btn-light-primary dropdown-toggle ms-3"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {filterOption === "all"
                ? "Todos"
                : filterOption === "pending"
                  ? "Em aberto"
                  : "Pago"}
            </button>
            <ul className="dropdown-menu">
              <li>
                <button
                  type="button"
                  className={`dropdown-item ${filterOption === "all" ? "active" : ""}`}
                  onClick={() => setFilterOption("all")}
                >
                  Todos
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={`dropdown-item ${filterOption === "pending" ? "active" : ""}`}
                  onClick={() => setFilterOption("pending")}
                >
                  Em aberto
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={`dropdown-item ${filterOption === "completed" ? "active" : ""}`}
                  onClick={() => setFilterOption("completed")}
                >
                  Pago
                </button>
              </li>
            </ul>
          </div>
          <hr />
        </>
      )}

      <div className="container">
        {filteredOrders.map((order, index) => {
          const status = normalizeStatus(order.status_pedido);
          const name = resolveName(order);
          const userId =
            Number(resolveUser(order)?._id ?? resolveUser(order)?.id) ||
            getOrderClientUserId(order);
          return (
            <div
              className="row align-items-center bg-light-dark rounded mb-1 p-1"
              key={`${order.id}-${index}`}
            >
              <div className="d-flex col align-items-center">
                <span
                  className="svg-icon svg-icon-warning me-5"
                  onClick={() => openPhoto(order)}
                  title="Ampliar foto"
                >
                  <img
                    style={{
                      cursor: "pointer",
                      border: `3px solid ${getUserBraceletColor(order)}`,
                      width: "3em",
                      height: "3em",
                      borderRadius: "3em",
                    }}
                    className="p-0 m-0"
                    alt={name}
                    src={getUserImageURL(order)}
                  />
                </span>
                <p
                  className="fw-bold text-hover-primary fs-6 m-0"
                  style={{ cursor: userId ? "pointer" : "default" }}
                  onClick={() => openCadastro(order)}
                  title={userId ? "Abrir cadastro do cliente" : undefined}
                >
                  {name}
                </p>
              </div>
              <div className="col">
                <span className="fw-bold text-muted">
                  {formatOrderDateTime(order)}
                </span>
              </div>
              <div className="col">
                <span
                  className={`fw-bold ${
                    status === "Pago" ? "text-green" : "text-yellow"
                  }`}
                >
                  {status}
                </span>
              </div>
              <div className="col">
                <Print
                  value={order.valor_total_pedido}
                  order={order}
                  clientName={name}
                />
              </div>
            </div>
          );
        })}
      </div>

      <ClienteDadosModal
        show={modalCadastro}
        clienteId={selectedUserId}
        onClose={() => {
          setModalCadastro(false);
          setSelectedUserId(null);
        }}
      />

      <Modal
        show={modalPicture}
        centered={true}
        dialogClassName="dialogModalPicture"
        className="border-none"
        contentClassName="contentModalPicture"
        style={{ backgroundColor: "#ffffff91" }}
        onHide={() => setModalPicture(false)}
      >
        <CloseButton
          onClick={() => setModalPicture(false)}
          style={{ marginLeft: "80%", width: "40px" }}
        />
        <Modal.Body>
          <div className="text-center">
            {selectedUser?.profile_image ? (
              <img
                style={{
                  border: `3px solid ${
                    selectedUser?.bracelets?.[0]?.bracelet_color?.color || "#FFF"
                  }`,
                  width: "320px",
                  height: "320px",
                  borderRadius: "180px",
                }}
                src={
                  String(selectedUser.profile_image).startsWith("http") ||
                  String(selectedUser.profile_image).startsWith("data:")
                    ? selectedUser.profile_image
                    : `${PATH_URL}${selectedUser.profile_image}`
                }
                alt={`Imagem de ${formatUserFullName(selectedUser)}`}
              />
            ) : (
              <img
                style={{ width: "300px", height: "300px", borderRadius: "8px" }}
                src={toAbsoluteUrl("/media/avatars/blank.png")}
                alt=""
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export { ListUserPaymentHistory };
